/**
 * Reservation Products Logic
 * Handles pre-order products with complex validation rules
 */

(function() {
  'use strict';

  const ReservationProducts = {
    // Cache for product reservation data per container
    _productDataCache: {},
    
    /**
     * Format date as Japanese period (上旬/中旬/下旬)
     */
    formatJapanesePeriod: function(dateStr) {
      if (!dateStr) return null;
      
      try {
        const date = new Date(dateStr);
        // Validate date
        if (isNaN(date.getTime())) return null;
        
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const day = date.getDate();
        
        let period = '';
        if (day <= 10) {
          period = '上旬';
        } else if (day <= 20) {
          period = '中旬';
        } else {
          period = '下旬';
        }
        
        return { year, month, day, period };
      } catch (e) {
        console.error('Error formatting Japanese period:', e);
        return null;
      }
    },

    /**
     * Format shipping date range in Japanese
     */
    formatShippingRange: function(startDateStr, endDateStr) {
      if (!startDateStr || !endDateStr) return '';
      
      const start = this.formatJapanesePeriod(startDateStr);
      const end = this.formatJapanesePeriod(endDateStr);
      
      if (!start || !end) return '';
      
      // If same month and year
      if (start.year === end.year && start.month === end.month) {
        if (start.period === end.period) {
          return `${start.year}年${start.month}月${end.period}頃`;
        } else {
          return `${start.year}年${start.month}月${start.period}〜${end.period}頃`;
        }
      }
      
      // Different months
      if (start.year === end.year) {
        return `${start.year}年${start.month}月${start.period}〜${end.month}月${end.period}頃`;
      }
      
      // Different years
      return `${start.year}年${start.month}月${start.period}〜${end.year}年${end.month}月${end.period}頃`;
    },

    /**
     * Check if two date ranges overlap
     */
    dateRangesOverlap: function(start1, end1, start2, end2) {
      const s1 = new Date(start1).getTime();
      const e1 = new Date(end1).getTime();
      const s2 = new Date(start2).getTime();
      const e2 = new Date(end2).getTime();
      
      return s1 <= e2 && s2 <= e1;
    },

    /**
     * Check if a date is before another date
     */
    isDateBefore: function(date, compareDate) {
      return new Date(date).getTime() < new Date(compareDate).getTime();
    },

    /**
     * Check if current date is between two dates
     */
    isBetweenDates: function(startDate, endDate) {
      const now = new Date().getTime();
      const start = startDate ? new Date(startDate).getTime() : -Infinity;
      const end = endDate ? new Date(endDate).getTime() : Infinity;
      
      return now >= start && now <= end;
    },

    /**
     * Validate if product can be added to cart
     */
    validateProduct: function(productData, cartItems) {
      const now = new Date().getTime();
      const result = {
        canAddToCart: true,
        buttonText: 'カートに入れる',
        buttonClass: 'button--green',
        message: '',
        shippingText: '',
        conflictingItems: []
      };

      // Validate input
      if (!productData) {
        console.error('Product data is missing');
        return result;
      }

      cartItems = cartItems || [];

      // Filter out the same product from cart items to allow re-adding
      const currentProductUrl = productData.productUrl;
      const otherCartItems = cartItems.filter(item => item.url !== currentProductUrl);

      // Calculate shipping text FIRST (before any early returns)
      // Display ONLY the current product's shipping dates (don't consider cart items)
      if (productData.shippingStart && productData.shippingEnd) {
        result.shippingText = this.formatShippingRange(productData.shippingStart, productData.shippingEnd);
      }

      // Check license end date first
      if (productData.licenseEnd) {
        try {
          const licenseEnd = new Date(productData.licenseEnd).getTime();
          if (!isNaN(licenseEnd) && now > licenseEnd) {
            result.canAddToCart = false;
            result.buttonText = '販売終了';
            result.buttonClass = 'button--disabled';
            return result;
          }
        } catch (e) {
          console.error('Error parsing license end date:', e);
        }
      }

      // Check order start and end dates
      if (productData.orderStart && productData.orderEnd) {
        try {
          const orderStart = new Date(productData.orderStart).getTime();
          const orderEnd = new Date(productData.orderEnd).getTime();
          
          if (!isNaN(orderStart) && !isNaN(orderEnd)) {
            if (now < orderStart) {
              result.canAddToCart = false;
              result.buttonText = '予約開始前';
              result.buttonClass = 'button--disabled';
              return result;
            }
            
            if (now > orderEnd) {
              result.canAddToCart = false;
              result.buttonText = '予約終了';
              result.buttonClass = 'button--disabled';
              return result;
            }
          }
        } catch (e) {
          console.error('Error parsing order dates:', e);
        }
      }

      // Check for cant_order_with_others conflicts
      // If current product has the flag and cart has OTHER products, block
      if (productData.cantOrderWithOthers && otherCartItems.length > 0) {
        result.conflictingItems = otherCartItems.map(item => ({
          title: item.title,
          url: item.url,
          variantId: item.variantId
        }));
        result.canAddToCart = false;
        result.buttonText = '注文不可';
        result.buttonClass = 'button--disabled';
        result.message = '現在カート内の商品と同時にご購入いただけません。ご購入をご希望の場合は、カートを空にしてください。';
        return result;
      }

      // Check if cart has any cant_order_with_others items (excluding current product)
      const cartCantOrderItems = otherCartItems.filter(item => item.cantOrderWithOthers);
      if (cartCantOrderItems.length > 0) {
        result.conflictingItems = cartCantOrderItems.map(item => ({
          title: item.title,
          url: item.url,
          variantId: item.variantId
        }));
        result.canAddToCart = false;
        result.buttonText = '注文不可';
        result.buttonClass = 'button--disabled';
        result.message = '現在カート内の商品と同時にご購入いただけません。ご購入をご希望の場合は、カートを空にしてください。';
        return result;
      }

      // Check license product conflicts (only with other products)
      if (productData.shippingStart && productData.shippingEnd) {
        for (const item of otherCartItems) {
          if (item.licenseEnd) {
            // Check if shipping period does NOT overlap with license end date
            // Shipping period overlaps if license end is >= shipping start AND <= shipping end
            const shippingStart = new Date(productData.shippingStart).getTime();
            const shippingEnd = new Date(productData.shippingEnd).getTime();
            const licenseEnd = new Date(item.licenseEnd).getTime();
            
            // Block if shipping starts AFTER license ends (no overlap)
            if (shippingStart > licenseEnd) {
              result.conflictingItems.push({
                title: item.title,
                url: item.url,
                variantId: item.variantId
              });
              result.canAddToCart = false;
              result.buttonText = '注文不可';
              result.buttonClass = 'button--disabled';
              result.message = '現在カート内の商品と同時にご購入いただけません。ご購入をご希望の場合は、カートを空にしてください。';
              return result;
            }
          }
        }
      }

      // If product has license end, check against other cart items' shipping dates
      if (productData.licenseEnd) {
        for (const item of otherCartItems) {
          if (item.shippingStart && item.shippingEnd) {
            const licenseEnd = new Date(productData.licenseEnd).getTime();
            const itemShippingStart = new Date(item.shippingStart).getTime();
            
            // Block if cart item's shipping starts AFTER license ends (no overlap)
            if (itemShippingStart > licenseEnd) {
              result.conflictingItems.push({
                title: item.title,
                url: item.url,
                variantId: item.variantId
              });
              result.canAddToCart = false;
              result.buttonText = '注文不可';
              result.buttonClass = 'button--disabled';
              result.message = '現在カート内の商品と同時にご購入いただけません。ご購入をご希望の場合は、カートを空にしてください。';
              return result;
            }
          }
        }
      }

      // Check stock availability at the end (after more specific checks)
      if (productData.available === false) {
        result.canAddToCart = false;
        result.buttonText = '在庫切れ';
        result.buttonClass = 'button--disabled';
        return result;
      }

      return result;
    },

    /**
     * Update shipping date order attribute input
     */
    updateShippingAttribute: function(shippingText) {
      // Find all shipping attribute inputs in the page
      const inputs = document.querySelectorAll('[data-reservation-shipping-date]');
      inputs.forEach(input => {
        input.value = shippingText || '';
      });
    },

    /**
     * Format and display individual product shipping dates
     */
    formatProductShippingDisplays: function() {
      const displays = document.querySelectorAll('[data-product-shipping-display]');
      
      displays.forEach(display => {
        const shippingStart = display.getAttribute('data-shipping-start');
        const shippingEnd = display.getAttribute('data-shipping-end');
        const licenseEnd = display.getAttribute('data-license-end');
        
        let formattedText = '';
        
        // Priority to license end date
        if (licenseEnd) {
          formattedText = this.formatShippingRange(licenseEnd, licenseEnd);
        } else if (shippingStart && shippingEnd) {
          formattedText = this.formatShippingRange(shippingStart, shippingEnd);
        }
        
        if (formattedText) {
          display.textContent = formattedText;
        }
      });
    },

    /**
     * Calculate cart shipping info for checkout validation
     */
    calculateCartShipping: function(cartItems) {
      if (!cartItems || cartItems.length === 0) {
        return { shouldShowModal: false, shippingText: '' };
      }

      let latestShippingStart = null;
      let latestShippingEnd = null;
      let latestShippingEndTime = 0;
      
      // Track unique dates to check if they differ
      const uniqueDates = new Set();

      // Find the latest shipping date and track unique ranges
      // Note: License end dates are NOT prioritized for order emails - only shipping dates
      for (const item of cartItems) {
        if (item.shippingEnd) {
          const shippingEndTime = new Date(item.shippingEnd).getTime();
          // Use shipping range as unique identifier (start+end)
          const rangeId = (item.shippingStart || '') + '|' + item.shippingEnd;
          uniqueDates.add(rangeId);
          
          if (shippingEndTime > latestShippingEndTime) {
            latestShippingEndTime = shippingEndTime;
            latestShippingEnd = item.shippingEnd;
            latestShippingStart = item.shippingStart || item.shippingEnd;
          }
        }
      }

      if (!latestShippingEnd) {
        return { shouldShowModal: false, shippingText: '' };
      }

      const shippingText = this.formatShippingRange(latestShippingStart, latestShippingEnd);
      
      // Show modal if there are multiple items
      return {
        shouldShowModal: cartItems.length >= 2,
        shippingText: shippingText
      };
    },

    /**
     * Render product buy button
     */
    renderProductButton: function(containerSelector) {
      const container = document.querySelector(containerSelector);
      if (!container) return;

      let productData, cartData;
      
      // ALWAYS prioritize cached data if available (prevents re-reading stale data from DOM)
      if (this._productDataCache[containerSelector]) {
        // Use cached data (which is kept up-to-date with variant changes)
        const cached = this._productDataCache[containerSelector];
        productData = cached.productData;
        cartData = cached.cartData;
      } else {
        // First time: read from element and cache it
        const productDataEl = container.querySelector('[data-product-reservation]');
        if (!productDataEl) return;
        
        try {
          productData = JSON.parse(productDataEl.getAttribute('data-product-reservation'));
          cartData = JSON.parse(productDataEl.getAttribute('data-cart-items') || '[]');
          
          // Cache the data for future renders
          this._productDataCache[containerSelector] = {
            productData: productData,
            cartData: cartData
          };
        } catch (e) {
          console.error('Error parsing product data:', e);
          return;
        }
      }
      
      try {
        const validation = this.validateProduct(productData, cartData);
        
        let html = '';
        
        // Show conflict message if needed
        if (validation.message && validation.conflictingItems.length > 0) {
          html += '<div style="margin-bottom: 20px;">';
          html += `<p>${validation.message}</p>`;
          html += '<ul style="margin: 10px 0; padding-left: 20px;">';
          validation.conflictingItems.forEach(item => {
            html += `<li><a href="${item.url}" target="_blank" style="text-decoration: underline; color: #1976D2;">${item.title}</a></li>`;
          });
          html += '</ul>';
          html += '</div>';
        }
        
        // Write shipping text to the dedicated display element (between product name and quantity selector)
        const shippingDisplayEl = document.querySelector('[data-reservation-shipping-display-product]');
        if (shippingDisplayEl) {
          shippingDisplayEl.innerHTML = validation.shippingText
            ? `<p class="product-reservation-shipping-text">${validation.shippingText}に発送予定</p>`
            : '';
        }

        // Update the shipping attribute for order tracking (or clear it)
        this.updateShippingAttribute(validation.shippingText);
        
        // Show button
        html += '<div class="product-form__payment-container">';
        html += `<button type="submit" class="product-form__add-button button ${validation.buttonClass} ${validation.canAddToCart ? '' : 'button--disabled'}" `;
        html += validation.canAddToCart ? 'data-action="add-to-cart"' : 'disabled';
        html += `>${validation.buttonText}</button>`;
        html += '</div>';
        
        container.innerHTML = html;
      } catch (e) {
        console.error('Error rendering product button:', e);
      }
    },

    /**
     * Setup cart checkout modal
     */
    setupCartCheckout: function(containerSelector) {
      const container = document.querySelector(containerSelector);
      if (!container) return;

      const cartDataEl = container.querySelector('[data-cart-reservation]');
      if (!cartDataEl) return;

      try {
        const cartDataStr = cartDataEl.getAttribute('data-cart-reservation');
        if (!cartDataStr) {
          // No cart data, clear the shipping attribute
          this.updateShippingAttribute('');
          return;
        }
        
        const cartData = JSON.parse(cartDataStr);
        const shippingInfo = this.calculateCartShipping(cartData);
        
        // Update shipping attribute regardless of modal
        this.updateShippingAttribute(shippingInfo.shippingText || '');
        
        if (!shippingInfo.shouldShowModal) return;

        // Find the checkout button
        const checkoutButton = container.querySelector('.cart-recap__checkout');
        if (!checkoutButton) return;

        // Prevent duplicate initialization
        if (container.querySelector('.cart-checkout-modal-trigger')) return;
        
        // Also check if button already has the modal
        if (checkoutButton.classList.contains('btn-checkout-original')) return;

        // Create modal trigger button
        const triggerButton = checkoutButton.cloneNode(true);
        triggerButton.type = 'button';
        triggerButton.classList.add('cart-checkout-modal-trigger');
        triggerButton.removeAttribute('name'); // Remove 'checkout' name to prevent form submission
        
        // Hide original button
        checkoutButton.style.display = 'none';
        checkoutButton.classList.add('btn-checkout-original');
        
        // Insert trigger button before original
        checkoutButton.parentNode.insertBefore(triggerButton, checkoutButton);
        
        // Create modal HTML
        const modalHTML = `
          <div class="cart-checkout-modal-overlay cart-checkout-modal">
            <div class="cart-checkout-modal-overlay-close"></div>
            
            <div class="cart-checkout-modal-content">
              <div class="cart-checkout-modal-body">
                <p>カート内に発送日の異なる商品がございます。</p>
                <p>最も遅い発送日に合わせて発送いたします。</p>
                <p><strong>発送予定：${shippingInfo.shippingText}</strong></p>
                
                <button type="button" class="button button--primary cart-checkout-modal-confirm-checkout">確認しました</button>
              </div>
            </div>
          </div>
        `;
        
        // Insert modal after trigger button
        triggerButton.insertAdjacentHTML('afterend', modalHTML);
        
        // Add modal styles
        if (!document.getElementById('cart-checkout-modal-styles')) {
          const styles = document.createElement('style');
          styles.id = 'cart-checkout-modal-styles';
          styles.textContent = `
            .cart-checkout-modal-overlay {
              position: fixed;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              background-color: rgba(0, 0, 0, 0.4);
              z-index: 999999;
              display: none;
              justify-content: center;
              align-items: center;
            }
            
            .cart-checkout-modal-overlay.show {
              display: flex;
            }
            
            .cart-checkout-modal-overlay-close {
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              cursor: pointer;
              z-index: 1;
            }
            
            .cart-checkout-modal-content {
              background: white;
              border-radius: 3px;
              max-width: 400px;
              width: 90%;
              padding: 30px;
              text-align: center;
              box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
              position: relative;
              z-index: 2;
            }
            
            .cart-checkout-modal-body p {
              margin: 0 0 15px 0;
              color: #333;
              font-size: 16px;
              line-height: 1.5;
            }
            
            .cart-checkout-modal-body p strong {
              font-size: 18px;
              font-weight: bold;
              color: #e65100;
              margin-bottom: 25px;
            }
            
            .cart-checkout-modal-body button {
              margin: 0;
              padding: 4px 30px;
              font-size: 16px;
            }
          `;
          document.head.appendChild(styles);
        }
        
        // Setup event listeners
        const modal = container.querySelector('.cart-checkout-modal');
        const overlayClose = modal.querySelector('.cart-checkout-modal-overlay-close');
        const confirmButton = modal.querySelector('.cart-checkout-modal-confirm-checkout');
        
        const closeModal = () => {
          modal.classList.remove('show');
          document.body.style.overflow = '';
        };
        
        const openModal = () => {
          modal.classList.add('show');
          document.body.style.overflow = 'hidden';
        };
        
        triggerButton.addEventListener('click', (e) => {
          e.preventDefault();
          openModal();
        });
        
        overlayClose.addEventListener('click', closeModal);
        
        modal.addEventListener('click', (e) => {
          if (e.target === modal) {
            closeModal();
          }
        });
        
        confirmButton.addEventListener('click', () => {
          closeModal();
          checkoutButton.click();
        });
        
      } catch (e) {
        console.error('Error setting up cart checkout:', e);
      }
    },

    /**
     * Handle variant change event
     */
    handleVariantChange: function(event) {
      if (!event.detail || !event.detail.variant) return;
      
      const newVariant = event.detail.variant;
      const containerSelector = '[data-reservation-product-container]';
      
      // Update cached product data with new variant availability
      if (this._productDataCache[containerSelector]) {
        this._productDataCache[containerSelector].productData.available = newVariant.available;
      }
      
      // Re-render the button with updated availability
      this.renderProductButton(containerSelector);
    },

    /**
     * Initialize on page load
     */
    init: function() {
      // Wait for DOM to be ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.init());
        return;
      }

      // Initialize product page
      this.renderProductButton('[data-reservation-product-container]');
      
      // Listen for variant changes
      document.addEventListener('variant:changed', (event) => this.handleVariantChange(event));
      
      // Initialize cart page
      this.setupCartCheckout('[data-reservation-cart-container]');
      
      // Format individual product shipping displays
      this.formatProductShippingDisplays();
      
      // Initialize mini-cart with better handling
      this.initMiniCart();
    },

    /**
     * Initialize mini-cart with mutation observer
     */
    initMiniCart: function() {
      const self = this;
      
      // Try immediate initialization
      this.setupCartCheckout('[data-reservation-minicart-container]');
      
      // Watch for mini-cart visibility changes
      const miniCart = document.getElementById('mini-cart');
      if (miniCart) {
        // Use MutationObserver to watch for aria-hidden changes
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'attributes' && mutation.attributeName === 'aria-hidden') {
              const isVisible = miniCart.getAttribute('aria-hidden') === 'false';
              if (isVisible) {
                // Re-initialize when mini-cart becomes visible
                setTimeout(() => {
                  self.setupCartCheckout('[data-reservation-minicart-container]');
                  self.formatProductShippingDisplays();
                }, 50);
              }
            }
          });
        });
        
        observer.observe(miniCart, {
          attributes: true,
          attributeFilter: ['aria-hidden']
        });
      }
      
      // Also listen for custom cart update events (if theme uses them)
      document.addEventListener('cart:updated', () => {
        setTimeout(() => {
          self.setupCartCheckout('[data-reservation-minicart-container]');
          self.formatProductShippingDisplays();
        }, 100);
      });
      
      // Fallback: periodically check if mini-cart is visible and needs initialization
      let checkCount = 0;
      const checkInterval = setInterval(() => {
        checkCount++;
        if (checkCount > 20) { // Stop after 20 tries (10 seconds)
          clearInterval(checkInterval);
          return;
        }
        
        const container = document.querySelector('[data-reservation-minicart-container]');
        if (container && !container.querySelector('.cart-checkout-modal-trigger')) {
          const isVisible = miniCart && miniCart.getAttribute('aria-hidden') === 'false';
          if (isVisible) {
            self.setupCartCheckout('[data-reservation-minicart-container]');
            self.formatProductShippingDisplays();
          }
        }
      }, 500);
    }
  };

  // Auto-initialize
  ReservationProducts.init();

  // Expose globally for manual initialization if needed
  window.ReservationProducts = ReservationProducts;
})();
